/**
 * Password Validation Utility for Client-Side
 * Provides real-time password strength checking
 * SECURITY FIX: Enhanced for Issues #12-22 - Empty password validation
 * Created: 2025-10-09
 */

export interface PasswordValidationResult {
  isValid: boolean;
  score: number; // 0-100
  errors: string[];
  warnings: string[];
  strength: 'Very Weak' | 'Weak' | 'Fair' | 'Good' | 'Strong';
}

export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
}

const defaultPolicy: PasswordPolicy = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
};

const commonPasswords = [
  '123456', 'password', '123456789', '12345678', '12345',
  '1234567', '1234567890', 'qwerty', 'abc123', '111111',
  '123123', 'admin', 'letmein', 'welcome', 'monkey',
  'dragon', 'pass', 'master', 'hello', 'freedom',
  'whatever', 'qazwsx', 'trustno1', 'jordan', 'harley',
  'user', 'Password', 'password123', 'admin123' // Added more common passwords
];

/**
 * SECURITY FIX: Enhanced password validation with empty/whitespace checks
 */
export function validatePassword(password: string, policy: PasswordPolicy = defaultPolicy): PasswordValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  let score = 0;

  // SECURITY FIX: Check for empty, null, or whitespace-only passwords
  if (!password) {
    errors.push('Password is required and cannot be empty');
    return {
      isValid: false,
      score: 0,
      errors,
      warnings,
      strength: 'Very Weak'
    };
  }

  if (password.trim().length === 0) {
    errors.push('Password cannot be only whitespace characters');
    return {
      isValid: false,
      score: 0,
      errors,
      warnings,
      strength: 'Very Weak'
    };
  }

  if (password.length === 0) {
    errors.push('Password cannot be empty');
    return {
      isValid: false,
      score: 0,
      errors,
      warnings,
      strength: 'Very Weak'
    };
  }

  // Check minimum length
  if (password.length < policy.minLength) {
    errors.push(`Password must be at least ${policy.minLength} characters long`);
  } else {
    score += 20;
    // Bonus for extra length
    if (password.length >= 12) score += 10;
    if (password.length >= 16) score += 10;
  }

  // Check complexity requirements
  if (policy.requireUppercase) {
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    } else {
      score += 15;
    }
  }

  if (policy.requireLowercase) {
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    } else {
      score += 15;
    }
  }

  if (policy.requireNumbers) {
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    } else {
      score += 15;
    }
  }

  if (policy.requireSpecialChars) {
    if (!/[@$!%*?&]/.test(password)) {
      errors.push('Password must contain at least one special character (@$!%*?&)');
    } else {
      score += 15;
    }
  }

  // Check against common passwords
  if (commonPasswords.includes(password.toLowerCase())) {
    errors.push('This password is too common. Please choose a different password');
    score = Math.min(score, 20); // Cap score for common passwords
  }

  // Check for patterns
  if (hasRepeatingChars(password)) {
    warnings.push('Avoid repeating characters');
    score -= 5;
  }

  if (hasSequentialChars(password)) {
    warnings.push('Avoid sequential characters (e.g., 123, abc)');
    score -= 5;
  }

  // Additional complexity bonuses
  const uniqueChars = new Set(password).size;
  if (uniqueChars >= password.length * 0.7) {
    score += 10; // Bonus for character diversity
  }

  // Mix of character types bonus
  const charTypes = [
    /[a-z]/.test(password),
    /[A-Z]/.test(password),
    /\d/.test(password),
    /[^a-zA-Z\d]/.test(password)
  ].filter(Boolean).length;

  if (charTypes >= 3) score += 10;
  if (charTypes === 4) score += 5;

  // Ensure score is within bounds
  score = Math.max(0, Math.min(100, score));

  const strength = getStrengthLabel(score);
  const isValid = errors.length === 0 && score >= 60;

  return {
    isValid,
    score,
    errors,
    warnings,
    strength,
  };
}

/**
 * Get password strength label based on score
 */
function getStrengthLabel(score: number): 'Very Weak' | 'Weak' | 'Fair' | 'Good' | 'Strong' {
  if (score < 20) return 'Very Weak';
  if (score < 40) return 'Weak';
  if (score < 60) return 'Fair';
  if (score < 80) return 'Good';
  return 'Strong';
}

/**
 * Check for repeating characters (3+ in a row)
 */
function hasRepeatingChars(password: string): boolean {
  return /(.)\1{2,}/.test(password);
}

/**
 * Check for sequential characters
 */
function hasSequentialChars(password: string): boolean {
  const sequences = ['123', '234', '345', '456', '567', '678', '789', '890',
                     'abc', 'bcd', 'cde', 'def', 'efg', 'fgh', 'ghi', 'hij',
                     'qwe', 'wer', 'ert', 'rty', 'tyu', 'yui', 'uio', 'iop'];
  
  const lowerPassword = password.toLowerCase();
  return sequences.some(seq => lowerPassword.includes(seq));
}

/**
 * Get strength color for UI
 */
export function getStrengthColor(strength: string): string {
  switch (strength) {
    case 'Very Weak': return '#f44336';
    case 'Weak': return '#ff9800';
    case 'Fair': return '#ffeb3b';
    case 'Good': return '#8bc34a';
    case 'Strong': return '#4caf50';
    default: return '#757575';
  }
}

/**
 * Generate password suggestions
 */
export function generatePasswordSuggestions(): string[] {
  const suggestions = [
    'Use a mix of uppercase and lowercase letters',
    'Include numbers and special characters',
    'Make it at least 12 characters long',
    'Avoid using personal information',
    'Consider using a passphrase with multiple words',
    'Use unique passwords for each account',
  ];

  return suggestions;
}

/**
 * SECURITY FIX: Enhanced SMTP password validation for email configuration
 */
export function validateSMTPPassword(password: string): PasswordValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // SECURITY FIX: Check for empty or whitespace-only SMTP passwords
  if (!password) {
    errors.push('SMTP password is required and cannot be empty');
    return {
      isValid: false,
      score: 0,
      errors,
      warnings,
      strength: 'Very Weak'
    };
  }

  if (password.trim().length === 0) {
    errors.push('SMTP password cannot be only whitespace characters');
    return {
      isValid: false,
      score: 0,
      errors,
      warnings,
      strength: 'Very Weak'
    };
  }

  if (password.length === 0) {
    errors.push('SMTP password cannot be empty');
    return {
      isValid: false,
      score: 0,
      errors,
      warnings,
      strength: 'Very Weak'
    };
  }

  // Additional SMTP-specific validation
  if (password !== password.trim()) {
    warnings.push('SMTP password should not start or end with whitespace');
  }

  // Check for minimum requirements (less strict than user passwords)
  if (password.length < 1) {
    errors.push('SMTP password must have at least 1 character');
  }

  return {
    isValid: errors.length === 0,
    score: errors.length === 0 ? 100 : 0,
    errors,
    warnings,
    strength: errors.length === 0 ? 'Good' : 'Very Weak'
  };
}

/**
 * SECURITY FIX: Quick validation for empty passwords in forms
 */
export function isPasswordEmpty(password: string): boolean {
  return !password || password.trim().length === 0;
}